import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  useTheme,
  Skeleton,
  Paper,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Button,
  Link,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Download,
  Gavel,
  Source as SourceIcon,
  History,
  Assessment,
  ArrowUpward,
  ArrowDownward,
  Close,
  OpenInNew,
  CalendarToday,
  Event as EventIcon,
  FilterList,
  Email,
  Send,
  PictureAsPdf,
  Link as LinkIcon,
  Search,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';

import { apiService } from '../services/api';
import type { SourceSummary } from '../services/api';
import {
  addOfficialDaysISO,
  formatOfficialDateBR,
  formatOfficialDateShortBR,
  officialDateKey,
  todayOfficialDateKey,
} from '../utils/officialDate';
import { RootState } from '../store';
import { canonicalSourceBucket } from '../utils/sourceBuckets';
import { MudancasLegislativasTableSection } from '../components/MudancasLegislativasTableSection';
import type { ChangesColumnFilterKey } from '../components/ChangesTable';
import { EMPTY_CHANGES_COLUMN_FILTERS } from '../components/ChangesTable';
import { useChangesColumnFilterCatalogs } from '../hooks/useChangesColumnFilterCatalogs';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useSourcesSummary } from '../hooks/useSourcesSummary';
import { VerDetalhesComDataPublicacao } from '../components/VerDetalhesComDataPublicacao';
import { useChangesTable } from '../hooks/useChangesTable';
import { useSyncDashboardAgencyInterest } from '../hooks/useSyncDashboardAgencyInterest';
import { useAgencyMonitoringSuite } from '../hooks/useAgencyMonitoringSuite';
import { AgencyMonitoringSuiteDialogs } from '../components/monitoring';
import { MudancasMonitoramentoFiltros } from '../components/MudancasMonitoramentoFiltros';
import { FiltroPorPeriodo } from '../components/FiltroPorPeriodo';
import type { FiltroPorPeriodoValue } from '../components/FiltroPorPeriodo';
import { getDefaultPeriodRange, periodToApiRange, chartTimelineRangeEnd } from '../utils/defaultDateRange';
import {
  buildChangesQueryParams,
  buildStatsQueryInput,
  type DashboardChartKey,
  type DashboardSearchContext,
} from '../utils/dashboardSearchParams';
import { resolveAgencyKeysFromNames, buildAgencyNameToKeyMap } from '../utils/buildAgencyNameToKeyMap';
import { resolveChangeAssuntoDisplay } from '../utils/changeAssunto';
import { lawTypeSelectionLabels } from '../utils/lawTypeFilter';
import { normalizeAssuntoKey } from '../utils/assuntoMonitoring';
import { ChangeDetailLogsPanel } from '../components/ChangeDetailLogsPanel';
import { GreeningTabPanel } from '../components/greening/GreeningTabPanel';
import { GreeningChatFab } from '../components/greening/GreeningChatFab';
import { GreeningBrandLabel } from '../components/greening/GreeningBrandLabel';
import { GreeningIcon } from '../components/greening/GreeningIcon';
import { useGreeningAnalysis } from '../hooks/useGreeningAnalysis';
import { greeningQuestionCount } from '../components/greening/greeningQuestionCount';
import { ChangeEmailSendDialog } from '../components/ChangeEmailSendDialog';
import { ChangeFocusOutsideFiltersBanner } from '../components/ChangeFocusOutsideFiltersBanner';
import { filterAndZeroStateSourceSummaries } from '../utils/stateSourceSummaryCatalog';
import { useChangesTableFocus } from '../hooks/useChangesTableFocus';
import { DashboardChartCardShell } from '../components/dashboard/DashboardChartCardShell';
import { DistributionChartsPanel } from '../components/dashboard/DistributionChartsPanel';
import { CHANGE_TYPE_CATEGORIES, LAW_TYPE_CATEGORIES } from '../constants/dashboardChartCategories';
import { DashboardKpiCard } from '../components/dashboard/DashboardKpiCard';

const COLORS = ['#2E7D32', '#1976D2', '#F57C00', '#D32F2F', '#7B1FA2', '#00897B', '#E64A19'];

function fillPublicationDaysInRange(
  countByDate: Record<string, number>,
  startKey: string,
  endKey: string,
): Array<{ date: string; count: number }> {
  const days: Array<{ date: string; count: number }> = [];
  let current = startKey;
  while (current <= endKey) {
    days.push({ date: current, count: countByDate[current] ?? 0 });
    if (current === endKey) break;
    current = addOfficialDaysISO(current, 1);
  }
  return days;
}

export function DashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [cardDetailsOpen, setCardDetailsOpen] = useState(false);
  const [cardDetailsType, setCardDetailsType] = useState<'today' | 'month' | 'total' | 'sources' | null>(null);
  const [cardDetailsList, setCardDetailsList] = useState<any[]>([]);
  const [cardDetailsPage, setCardDetailsPage] = useState(1);
  const [cardDetailsTotal, setCardDetailsTotal] = useState(0);
  const [cardDetailsTotalPages, setCardDetailsTotalPages] = useState(0);
  const [cardDetailsLoading, setCardDetailsLoading] = useState(false);
  const CARD_DETAILS_PAGE_SIZE = 10;
  const [sourceChartPeriod, setSourceChartPeriod] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [timelineChartPeriod, setTimelineChartPeriod] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [typeChartPeriod, setTypeChartPeriod] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [lawTypeChartPeriod, setLawTypeChartPeriod] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [chartUseGlobalPeriod, setChartUseGlobalPeriod] = useState<Record<DashboardChartKey, boolean>>({
    source: false,
    timeline: false,
    type: false,
    lawType: false,
  });

  const { user } = useSelector((state: RootState) => state.auth);

  const agencyMonitoring = useAgencyMonitoringSuite();
  const [sourcesTab, setSourcesTab] = useState<'federal' | 'state' | 'municipal'>('federal');

  const { data: preferences } = useQuery(
    ['preferences', user?.id],
    () => apiService.getPreferences(user?.id || 1),
    { enabled: !!user?.id }
  );

  const preferredAgencies = React.useMemo((): string[] => {
    const raw =
      (preferences as { filters?: { sources?: string[] }; filters_json?: string | null } | undefined)
        ?.filters
      ?? preferences?.filters_json;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed?.sources) ? parsed.sources : [];
    } catch {
      return [];
    }
  }, [preferences]);

  // Hook para gerenciar estado da tabela
  const tableState = useChangesTable({ initialPage: 1, initialRowsPerPage: 10 });
  const [changesSortBy, setChangesSortBy] = useState<string>('published_at');
  const [changesSortOrder, setChangesSortOrder] = useState<'asc' | 'desc'>('desc');
  const [changesPeriodFilter, setChangesPeriodFilter] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [publicationDateMinCache, setPublicationDateMinCache] = useState<string | null>(null);
  const [monitoredNormAssuntoChanges, setMonitoredNormAssuntoChanges] = useState(false);
  const [monitoredAgencyChanges, setMonitoredAgencyChanges] = useState(false);
  const [greeningQaOnlyChanges, setGreeningQaOnlyChanges] = useState(false);
  const [assuntoFilterChanges, setAssuntoFilterChanges] = useState<string | null>(null);
  const [keywordFilterChanges, setKeywordFilterChanges] = useState<string | null>(null);
  const [columnFiltersResetNonce, setColumnFiltersResetNonce] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeDetailsTab, setChangeDetailsTab] = useState(0);
  const [tableRowsSignature, setTableRowsSignature] = useState('0:');
  const tableRowsRef = useRef<Array<{ id: number }>>([]);

  const closeChangeDetailsModal = useCallback(() => {
    setChangeDetailsTab(0);
    tableState.handleCloseDetails();
  }, [tableState.handleCloseDetails]);

  const changeDetailsLegislationId = tableState.selectedChange?.legislation_id;
  const changeDetailsGreeningEnabled = tableState.detailsOpen && Boolean(changeDetailsLegislationId);
  const changeDetailsGreening = useGreeningAnalysis(changeDetailsLegislationId, changeDetailsGreeningEnabled);

  const preferredAgencyKeys = React.useMemo((): string[] => {
    if (!preferences) return [];
    const filters = apiService.parsePreferenceFilters(preferences);
    const keys = Array.isArray(filters.agency_keys) ? filters.agency_keys.filter(Boolean) : [];
    if (keys.length > 0) return keys;
    return [];
  }, [preferences]);

  const [changesColumnFilters, setChangesColumnFilters] =
    useState<Record<ChangesColumnFilterKey, string[]>>(() => ({ ...EMPTY_CHANGES_COLUMN_FILTERS }));
  const changesColumnFilterSigRef = useRef<string | null>(null);
  const persistAgencyInterestRef = useRef<(names: string[]) => void>(() => {});
  const clearFocusHighlightRef = useRef<() => void>(() => {});
  const handleChangesColumnFiltersChange = useCallback(
    (f: Record<ChangesColumnFilterKey, string[]>) => {
      clearFocusHighlightRef.current();
      const sig = [f.state, f.agency, f.lawType, f.changeType].map((v) => v.join('|')).join('::');
      setChangesColumnFilters(f);
      if (changesColumnFilterSigRef.current !== null && changesColumnFilterSigRef.current !== sig) {
        tableState.handlePageChange(1);
      }
      changesColumnFilterSigRef.current = sig;
      persistAgencyInterestRef.current(f.agency);
    },
    [tableState.handlePageChange],
  );

  const clearAllColumnFilters = useCallback(() => {
    clearFocusHighlightRef.current();
    setChangesColumnFilters({ ...EMPTY_CHANGES_COLUMN_FILTERS });
    setColumnFiltersResetNonce((n) => n + 1);
    persistAgencyInterestRef.current([]);
    tableState.handlePageChange(1);
  }, [tableState.handlePageChange]);

  const handleMonitoredNormAssuntoToggle = useCallback(() => {
    clearFocusHighlightRef.current();
    setMonitoredNormAssuntoChanges((v) => !v);
    tableState.handlePageChange(1);
  }, [tableState.handlePageChange]);

  const handleMonitoredAgencyToggle = useCallback(() => {
    clearFocusHighlightRef.current();
    setMonitoredAgencyChanges((v) => !v);
    tableState.handlePageChange(1);
  }, [tableState.handlePageChange]);

  const updateColumnFilter = useCallback(
    (key: ChangesColumnFilterKey, values: string[]) => {
      clearFocusHighlightRef.current();
      setChangesColumnFilters((prev) => {
        const next = { ...prev, [key]: values };
        if (key === 'agency') persistAgencyInterestRef.current(values);
        return next;
      });
      tableState.handlePageChange(1);
    },
    [tableState.handlePageChange],
  );

  const columnFilterActiveChips = React.useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (changesColumnFilters.agency.length) {
      chips.push({
        key: 'col-agency',
        label: `Agência (coluna): ${changesColumnFilters.agency.join(', ')}`,
        onRemove: () => updateColumnFilter('agency', []),
      });
    }
    if (changesColumnFilters.state.length) {
      chips.push({
        key: 'col-state',
        label: `Estado: ${changesColumnFilters.state.join(', ')}`,
        onRemove: () => updateColumnFilter('state', []),
      });
    }
    if (changesColumnFilters.lawType.length) {
      chips.push({
        key: 'col-lawtype',
        label: `Tipo norma: ${lawTypeSelectionLabels(changesColumnFilters.lawType).join(', ')}`,
        onRemove: () => updateColumnFilter('lawType', []),
      });
    }
    if (changesColumnFilters.changeType.length) {
      chips.push({
        key: 'col-changetype',
        label: `Mudança: ${changesColumnFilters.changeType.join(', ')}`,
        onRemove: () => updateColumnFilter('changeType', []),
      });
    }
    return chips;
  }, [changesColumnFilters, updateColumnFilter]);

  const globalContextFiltersActive = Boolean(
    assuntoFilterChanges ||
      keywordFilterChanges ||
      changesColumnFilters.state.length ||
      changesColumnFilters.lawType.length ||
      changesColumnFilters.changeType.length,
  );

  const chartEmptyHint = useCallback(
    (chartKey: DashboardChartKey) => {
      if (chartUseGlobalPeriod[chartKey] && globalContextFiltersActive) {
        return 'Nenhuma mudança no período com os filtros globais ativos. Amplie o período ou limpe assunto/colunas.';
      }
      return 'Nenhuma mudança publicada neste período.';
    },
    [chartUseGlobalPeriod, globalContextFiltersActive],
  );

  const UF_TO_STATE: Record<string, string> = {
    ac: 'Acre',
    al: 'Alagoas',
    ap: 'Amapá',
    am: 'Amazonas',
    ba: 'Bahia',
    ce: 'Ceará',
    df: 'Distrito Federal',
    es: 'Espírito Santo',
    go: 'Goiás',
    ma: 'Maranhão',
    mt: 'Mato Grosso',
    ms: 'Mato Grosso do Sul',
    mg: 'Minas Gerais',
    pa: 'Pará',
    pb: 'Paraíba',
    pr: 'Paraná',
    pe: 'Pernambuco',
    pi: 'Piauí',
    rj: 'Rio de Janeiro',
    rn: 'Rio Grande do Norte',
    rs: 'Rio Grande do Sul',
    ro: 'Rondônia',
    rr: 'Roraima',
    sc: 'Santa Catarina',
    sp: 'São Paulo',
    se: 'Sergipe',
    to: 'Tocantins',
  };

  const groupSourcesByState = (list: any[]) => {
    const groups: Record<string, any[]> = {};
    list.forEach((s) => {
      const uf = String(s?.uf ?? '').toLowerCase();
      const stateName = UF_TO_STATE[uf] ?? (uf ? uf.toUpperCase() : 'Outros');
      if (!groups[stateName]) groups[stateName] = [];
      groups[stateName].push(s);
    });
    Object.keys(groups).forEach((k) => {
      groups[k] = groups[k].sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'pt-BR'));
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));
  };

  const activeAgencyNames = React.useMemo((): string[] => {
    if (changesColumnFilters.agency.length > 0) return changesColumnFilters.agency;
    return preferredAgencies;
  }, [changesColumnFilters.agency, preferredAgencies]);

  const publicationDateMin = publicationDateMinCache;
  const kpiStatsPeriod = React.useMemo(() => getDefaultPeriodRange(), []);

  const kpiStatsInput = React.useMemo(
    () =>
      buildStatsQueryInput(
        {
          period: kpiStatsPeriod,
          publicationDateMin,
          agencyNames: preferredAgencies,
          assunto: null,
          columnFilters: EMPTY_CHANGES_COLUMN_FILTERS,
        },
        { chartLocalOnly: true },
      ),
    [kpiStatsPeriod, publicationDateMin, preferredAgencies],
  );

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
    error: statsError,
    refetch: refetchStats,
  } = useDashboardStats(
    kpiStatsInput.dateRange,
    kpiStatsInput.agencies ?? (preferredAgencies.length > 0 ? preferredAgencies : undefined),
    { context: kpiStatsInput.context },
  );

  React.useEffect(() => {
    if (stats?.publication_date_min) {
      setPublicationDateMinCache(stats.publication_date_min);
    }
  }, [stats?.publication_date_min]);

  const statsPending = statsLoading && !stats;

  const {
    data: sourcesSummary,
    isLoading: isLoadingSourcesSummary,
    error: sourcesSummaryError,
    refetch: refetchSourcesSummaryFederal,
  } = useSourcesSummary('federal');

  const {
    data: sourcesSummaryState,
    isLoading: isLoadingSourcesSummaryState,
    error: sourcesSummaryStateError,
    refetch: refetchSourcesSummaryState,
  } = useSourcesSummary('state');

  const stateSourcesForModal = React.useMemo(
    () => filterAndZeroStateSourceSummaries((sourcesSummaryState || []) as any[]),
    [sourcesSummaryState],
  );

  const sourcesFontesAtivasCaption = React.useMemo(() => {
    if (!stats) return '—';
    const tfed = stats.total_sources_federal;
    const tmun = stats.total_sources_municipal;
    const afed = stats.active_sources_federal;
    const amun = stats.active_sources_municipal;
    if (tfed != null || tmun != null) {
      const a = (afed ?? 0) + (amun ?? 0);
      const t = (tfed ?? 0) + stateSourcesForModal.length + (tmun ?? 0);
      return `${a} ativa(s) · ${t} fonte(s) principal(is) no catálogo (BD)`;
    }
    return `${stats.active_sources ?? 0} ativa(s) · ${stats.total_sources ?? 0} fonte(s) principal(is) no catálogo (BD)`;
  }, [stats, stateSourcesForModal]);

  const fontesAtivasKpi = React.useMemo(() => {
    if (!stats) return { active: 0, total: 0 };
    const tfed = stats.total_sources_federal;
    const tmun = stats.total_sources_municipal;
    const afed = stats.active_sources_federal;
    const amun = stats.active_sources_municipal;
    if (tfed != null || tmun != null) {
      return {
        active: (afed ?? 0) + (amun ?? 0),
        total: (tfed ?? 0) + stateSourcesForModal.length + (tmun ?? 0),
      };
    }
    return { active: stats.active_sources ?? 0, total: stats.total_sources ?? 0 };
  }, [stats, stateSourcesForModal]);

  const {
    data: sourcesSummaryMunicipal,
    isLoading: isLoadingSourcesSummaryMunicipal,
    error: sourcesSummaryMunicipalError,
    refetch: refetchSourcesSummaryMunicipal,
  } = useSourcesSummary('municipal');

  const agencySummariesForKeys = React.useMemo((): SourceSummary[] => {
    return [
      ...(sourcesSummary || []),
      ...(sourcesSummaryState || []),
      ...(sourcesSummaryMunicipal || []),
    ];
  }, [sourcesSummary, sourcesSummaryState, sourcesSummaryMunicipal]);

  const { hydrateAgencyColumnFilter, persistAgencyColumnFilter } = useSyncDashboardAgencyInterest(
    user?.id,
    preferredAgencies,
    !!preferences,
    agencySummariesForKeys,
  );

  persistAgencyInterestRef.current = persistAgencyColumnFilter;

  React.useEffect(() => {
    hydrateAgencyColumnFilter(setChangesColumnFilters);
  }, [hydrateAgencyColumnFilter]);

  const resolvedAgencyKeys = React.useMemo((): string[] => {
    if (preferredAgencyKeys.length > 0) return preferredAgencyKeys;
    return resolveAgencyKeysFromNames(activeAgencyNames, buildAgencyNameToKeyMap(agencySummariesForKeys));
  }, [preferredAgencyKeys, activeAgencyNames, agencySummariesForKeys]);

  const dashboardSearchContext = React.useMemo(
    (): DashboardSearchContext => ({
      period: changesPeriodFilter,
      publicationDateMin,
      agencyNames: preferredAgencies,
      agencyKeys: resolvedAgencyKeys,
      assunto: assuntoFilterChanges,
      keyword: keywordFilterChanges,
      columnFilters: changesColumnFilters,
      monitoredNormAssunto: monitoredNormAssuntoChanges,
      monitoredAgency: monitoredAgencyChanges,
      greeningQaOnly: greeningQaOnlyChanges,
    }),
    [
      changesPeriodFilter,
      publicationDateMin,
      preferredAgencies,
      resolvedAgencyKeys,
      assuntoFilterChanges,
      keywordFilterChanges,
      changesColumnFilters,
      monitoredNormAssuntoChanges,
      monitoredAgencyChanges,
      greeningQaOnlyChanges,
    ],
  );

  const dashboardSearchContextRef = useRef(dashboardSearchContext);
  dashboardSearchContextRef.current = dashboardSearchContext;
  const getTableSearchContext = useCallback(() => dashboardSearchContextRef.current, []);
  const getTableRows = useCallback(() => tableRowsRef.current, []);

  const locationFocusChangeId =
    (location.state as { focusChangeId?: number } | null)?.focusChangeId ?? null;
  const onLocationFocusConsumed = useCallback(() => {
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [navigate, location.pathname, location.search]);

  const {
    highlightedChangeId,
    outsideFiltersBanner,
    focusContextOverride,
    clearFocusHighlight,
    showChangeInList,
    isResolvingFocus,
  } = useChangesTableFocus({
    searchParams,
    setSearchParams,
    locationFocusChangeId,
    onLocationFocusConsumed,
    getTableSearchContext,
    publicationDateMin,
    sortBy: changesSortBy,
    sortOrder: changesSortOrder,
    rowsPerPage: tableState.rowsPerPage,
    onPageChange: tableState.handlePageChange,
    getTableRows,
    tableRowsSignature,
    page: tableState.page,
  });

  const effectiveTableSearchContext = React.useMemo(
    (): DashboardSearchContext => focusContextOverride ?? dashboardSearchContext,
    [focusContextOverride, dashboardSearchContext],
  );

  clearFocusHighlightRef.current = clearFocusHighlight;

  const effectiveChartPeriod = useCallback(
    (key: DashboardChartKey): FiltroPorPeriodoValue => {
      if (chartUseGlobalPeriod[key]) return changesPeriodFilter;
      if (key === 'source') return sourceChartPeriod;
      if (key === 'timeline') return timelineChartPeriod;
      if (key === 'type') return typeChartPeriod;
      return lawTypeChartPeriod;
    },
    [
      chartUseGlobalPeriod,
      changesPeriodFilter,
      sourceChartPeriod,
      timelineChartPeriod,
      typeChartPeriod,
      lawTypeChartPeriod,
    ],
  );

  const handleChartPeriodChange = useCallback((key: DashboardChartKey, value: FiltroPorPeriodoValue) => {
    if (key === 'source') setSourceChartPeriod(value);
    if (key === 'timeline') setTimelineChartPeriod(value);
    if (key === 'type') setTypeChartPeriod(value);
    if (key === 'lawType') setLawTypeChartPeriod(value);
    setChartUseGlobalPeriod((prev) => ({ ...prev, [key]: false }));
  }, []);

  const buildChartStatsInput = React.useCallback(
    (key: DashboardChartKey) => {
      const period = effectiveChartPeriod(key);
      if (chartUseGlobalPeriod[key]) {
        return buildStatsQueryInput({
          period,
          publicationDateMin,
          agencyNames: preferredAgencies,
          assunto: assuntoFilterChanges,
          columnFilters: changesColumnFilters,
        });
      }
      return buildStatsQueryInput(
        {
          period,
          publicationDateMin,
          agencyNames: preferredAgencies,
          assunto: null,
          columnFilters: EMPTY_CHANGES_COLUMN_FILTERS,
        },
        { chartLocalOnly: true },
      );
    },
    [
      effectiveChartPeriod,
      chartUseGlobalPeriod,
      publicationDateMin,
      preferredAgencies,
      assuntoFilterChanges,
      changesColumnFilters,
    ],
  );

  const sourceChartStatsInput = React.useMemo(
    () => buildChartStatsInput('source'),
    [buildChartStatsInput],
  );

  const { data: statsBySource, isLoading: isLoadingBySource, isFetching: isFetchingBySource } =
    useDashboardStats(sourceChartStatsInput.dateRange, sourceChartStatsInput.agencies, {
      context: sourceChartStatsInput.context,
    });

  const timelineChartStatsInput = React.useMemo(
    () => buildChartStatsInput('timeline'),
    [buildChartStatsInput],
  );

  const { data: statsTimeline, isLoading: isLoadingTimeline, isFetching: isFetchingTimeline } =
    useDashboardStats(timelineChartStatsInput.dateRange, timelineChartStatsInput.agencies, {
      context: timelineChartStatsInput.context,
    });

  const typeChartStatsInput = React.useMemo(
    () => buildChartStatsInput('type'),
    [buildChartStatsInput],
  );

  const { data: statsByType, isLoading: isLoadingByType, isFetching: isFetchingByType } =
    useDashboardStats(typeChartStatsInput.dateRange, typeChartStatsInput.agencies, {
      context: typeChartStatsInput.context,
    });

  const lawTypeChartStatsInput = React.useMemo(
    () => buildChartStatsInput('lawType'),
    [buildChartStatsInput],
  );

  const { data: statsByLawType, isLoading: isLoadingByLawType, isFetching: isFetchingByLawType } =
    useDashboardStats(lawTypeChartStatsInput.dateRange, lawTypeChartStatsInput.agencies, {
      context: lawTypeChartStatsInput.context,
    });

  const changesCatalogs = useChangesColumnFilterCatalogs(!!stats);

  const { data: monitoredAssuntosData } = useQuery(
    ['assuntos-monitoring'],
    () => apiService.getAssuntosMonitoring(),
    { staleTime: 30_000 },
  );

  const monitoredAssuntoLabels = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const item of monitoredAssuntosData?.assuntos ?? []) {
      if (item.is_monitored && item.assunto_key) {
        map.set(item.assunto_key, item.assunto_label);
      }
    }
    return map;
  }, [monitoredAssuntosData?.assuntos]);

  const changesQueryParams = React.useMemo(
    () =>
      buildChangesQueryParams(effectiveTableSearchContext, {
        sort_by: changesSortBy,
        sort_order: changesSortOrder,
        page: tableState.page,
        page_size: tableState.rowsPerPage,
      }),
    [
      effectiveTableSearchContext,
      changesSortBy,
      changesSortOrder,
      tableState.page,
      tableState.rowsPerPage,
    ],
  );

  // Tabela paginada (10 por vez), filtrada no servidor quando necessário
  const { data: changesPage, isLoading: isLoadingChanges } = useQuery(
    ['dashboard-changes', changesQueryParams],
    () => apiService.getChanges(changesQueryParams as Parameters<typeof apiService.getChanges>[0]),
    { keepPreviousData: true, retry: 2, staleTime: 30_000 },
  );

  // Tabela: GET /changes (BD). Fallback recent_changes só no carregamento inicial (antes da 1ª resposta).
  const tableRows = React.useMemo(() => {
    if (changesPage !== undefined) {
      return changesPage.data ?? [];
    }
    const recent = stats?.recent_changes;
    if (!recent?.length) return [];
    return recent.map((r: {
      id: number;
      state: string;
      agency: string;
      agencies_linked?: string | null;
      law: string;
      date: string;
      change: string;
      published_at?: string | null;
      effective_date?: string | null;
      title: string;
      summary?: string | null;
      subject?: string | null;
      area_tematica?: string | null;
      url?: string | null;
      dou_ref?: string | null;
    }) => ({
      id: r.id,
      legislation_id: (r as { legislation_id?: number }).legislation_id,
      state: r.state,
      agency: r.agency,
      agencies_linked: r.agencies_linked ?? null,
      law: r.law,
      law_type: null,
      date: r.date,
      change: r.change,
      published_at: r.published_at,
      effective_date: r.effective_date,
      title: r.title,
      summary: r.summary,
      subject: r.subject ?? null,
      area_tematica: r.area_tematica ?? null,
      url: r.url,
      dou_ref: r.dou_ref,
      greening_status: (r as { greening_status?: string | null }).greening_status ?? null,
      greening_questions_count: (r as { greening_questions_count?: number }).greening_questions_count ?? 0,
    }));
  }, [changesPage, stats?.recent_changes]);

  tableRowsRef.current = tableRows;
  const liveQuestionCount = greeningQuestionCount(changeDetailsGreening.history);
  const seenQuestionCountsRef = React.useRef<Record<number, number>>({});
  if (
    changeDetailsLegislationId
    && !changeDetailsGreening.historyLoading
    && (
      changeDetailsGreening.history.length > 0
      || seenQuestionCountsRef.current[changeDetailsLegislationId] == null
    )
  ) {
    seenQuestionCountsRef.current[changeDetailsLegislationId] = liveQuestionCount;
  }
  const questionCountStamp = `${changeDetailsLegislationId ?? 0}:${changeDetailsGreening.historyLoading ? 1 : 0}:${liveQuestionCount}`;
  const tableRowsForDisplay = React.useMemo(() => {
    void questionCountStamp;
    const seen = seenQuestionCountsRef.current;
    return tableRows.map((row) => {
      const remembered = row.legislation_id != null ? seen[row.legislation_id] : undefined;
      if (remembered == null) return row;
      return { ...row, greening_questions_count: remembered };
    });
  }, [tableRows, questionCountStamp]);
  React.useEffect(() => {
    setTableRowsSignature(`${tableState.page}:${tableRows.map((row) => row.id).join(',')}`);
  }, [tableState.page, tableRows]);

  const tableTotalRows =
    changesPage !== undefined ? (changesPage.total ?? 0) : (stats?.total_changes ?? 0);
  const tableLoading = isLoadingChanges && changesPage === undefined;

  React.useEffect(() => {
    if (!tableState.detailsOpen) {
      setChangeDetailsTab(0);
    }
  }, [tableState.detailsOpen]);

  const handleTableRowClick = useCallback(
    (change: Parameters<typeof tableState.handleRowClick>[0]) => {
      clearFocusHighlight();
      tableState.handleRowClick(change);
    },
    [clearFocusHighlight, tableState.handleRowClick],
  );

  const handleTableViewDetails = useCallback(
    (change: Parameters<typeof tableState.handleViewDetails>[0]) => {
      clearFocusHighlight();
      tableState.handleViewDetails(change);
    },
    [clearFocusHighlight, tableState.handleViewDetails],
  );

  const handleTablePageChange = useCallback(
    (page: number) => {
      clearFocusHighlight();
      tableState.handlePageChange(page);
    },
    [clearFocusHighlight, tableState.handlePageChange],
  );

  const handleTableRowsPerPageChange = useCallback(
    (rowsPerPage: number) => {
      clearFocusHighlight();
      tableState.handleRowsPerPageChange(rowsPerPage);
    },
    [clearFocusHighlight, tableState.handleRowsPerPageChange],
  );

  // Período do próprio card (hoje / mês / ano), lido dos labels do KPI que o
  // backend já devolve. Usado no details para casar com o número do card.
  const cardDetailsPeriod = (type: 'today' | 'month' | 'total'): FiltroPorPeriodoValue => {
    const ymd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const now = new Date();
    if (type === 'today') {
      const t = ymd(now);
      return { start: t, end: t, preset: 'personalizado' };
    }
    if (type === 'month') {
      let y = now.getFullYear();
      let m = now.getMonth();
      const label = stats?.changes_kpi_month_label; // "MM/YYYY"
      if (label && /^\d{2}\/\d{4}$/.test(label)) {
        const [mm, yy] = label.split('/');
        m = parseInt(mm, 10) - 1;
        y = parseInt(yy, 10);
      }
      return { start: ymd(new Date(y, m, 1)), end: ymd(new Date(y, m + 1, 0)), preset: 'personalizado' };
    }
    // total = ano do card
    let y = now.getFullYear();
    const label = stats?.changes_kpi_year_label; // "YYYY"
    if (label && /^\d{4}$/.test(label)) y = parseInt(label, 10);
    const end = y === now.getFullYear() ? now : new Date(y, 11, 31);
    return { start: ymd(new Date(y, 0, 1)), end: ymd(end), preset: 'personalizado' };
  };

  // Carrega UMA página das mudanças no modal do card. O modal usa o PERÍODO DO
  // PRÓPRIO CARD (hoje/mês/ano), não o filtro global da tabela de baixo.
  const loadCardChangesPage = async (type: 'today' | 'month' | 'total', page: number) => {
    setCardDetailsLoading(true);
    try {
      const base = buildChangesQueryParams(
        { ...dashboardSearchContext, period: cardDetailsPeriod(type) },
        {
          sort_by: changesSortBy,
          sort_order: changesSortOrder,
          page,
          page_size: CARD_DETAILS_PAGE_SIZE,
        },
      );
      const response = await apiService.getChanges(base as Parameters<typeof apiService.getChanges>[0]);
      setCardDetailsList(response.data ?? []);
      setCardDetailsTotal(response.total ?? (response.data?.length ?? 0));
      setCardDetailsTotalPages(response.total_pages ?? 0);
      setCardDetailsPage(response.page ?? page);
    } catch {
      const fallback = stats?.recent_changes || [];
      setCardDetailsList(fallback);
      setCardDetailsTotal(fallback.length);
      setCardDetailsTotalPages(fallback.length ? 1 : 0);
      setCardDetailsPage(1);
    } finally {
      setCardDetailsLoading(false);
    }
  };

  const handleOpenCardDetails = async (type: 'today' | 'month' | 'total' | 'sources') => {
    setCardDetailsType(type);
    setCardDetailsOpen(true);
    setCardDetailsList([]);
    setCardDetailsPage(1);
    setCardDetailsTotal(0);
    setCardDetailsTotalPages(0);

    if (type === 'today' || type === 'month' || type === 'total') {
      await loadCardChangesPage(type, 1);
    } else if (type === 'sources') {
      void refetchSourcesSummaryFederal();
      void refetchSourcesSummaryState();
      void refetchSourcesSummaryMunicipal();
    }
  };

  const handleCloseCardDetails = () => {
    setCardDetailsOpen(false);
    setCardDetailsType(null);
    setCardDetailsList([]);
    setCardDetailsPage(1);
    setCardDetailsTotal(0);
    setCardDetailsTotalPages(0);
  };

  const handleTableSendEmail = async (emailData: { to: string; subject: string; item: any }) => {
    await tableState.handleSendEmail(emailData);
  };
  const parseLegislationData = (lawString: string) => {
    if (!lawString) return { type: '', number: '' };

    const match = lawString.match(/^(Lei|Decreto|Portaria|Resolução|Instrução Normativa|Norma|IN|Medida Provisória|MP|Ordem de Serviço|OS)\s*(.+)$/i);

    if (match) {
      let type = match[1];
      const number = match[2].trim();

      const typeUpper = type.toUpperCase();
      if (typeUpper === 'IN') type = 'Instrução Normativa';
      if (typeUpper === 'MP') type = 'Medida Provisória';
      if (typeUpper === 'OS') type = 'Ordem de Serviço';

      type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return { type, number };
    }

    return { type: 'Norma', number: lawString };
  };

  // Função para cor da legislação
  const getLegislationColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const typeUpper = type.toUpperCase();
    if (typeUpper.includes('LEI')) return 'primary';
    if (typeUpper.includes('DECRETO')) return 'secondary';
    if (typeUpper.includes('PORTARIA')) return 'info';
    if (typeUpper.includes('RESOLUÇÃO')) return 'success';
    if (typeUpper.includes('INSTRUÇÃO')) return 'warning';
    if (typeUpper.includes('MEDIDA')) return 'error';
    return 'default';
  };

  // Ramificação Criada/Atualizada/Revogada por período. Cada card de mudanças
  // usa o SEU recorte (hoje/mês/ano). Antes usava norms_by_type_total (quebra de
  // NORMAS), por isso os cards não batiam com a contagem de mudanças.
  const changeBreakdown = (byType?: Record<string, number>) =>
    CHANGE_TYPE_CATEGORIES.map((t) => ({
      label: t.label,
      value: Number(byType?.[t.key]) || 0,
    }));
  const changesTodayBreakdown = changeBreakdown(stats?.changes_by_type_today);
  const changesMonthBreakdown = changeBreakdown(stats?.changes_by_type_month);
  const changesYearBreakdown = changeBreakdown(stats?.changes_by_type_year);
  // Card «Normas Monitoradas» mantém a quebra de normas (não de mudanças).
  const normsBreakdownContextual = CHANGE_TYPE_CATEGORIES.map((t) => ({
    label: t.label,
    value: Number(stats?.norms_by_type_total?.[t.key]) || 0,
  }));

  const changesByAgencyData = React.useMemo(() => {
    const raw =
      statsBySource?.changes_by_agency
      ?? statsBySource?.changes_by_source
      ?? {};
    return Object.entries(raw)
      .map(([name, count]) => ({ name, value: count as number }))
      .sort((a, b) => b.value - a.value);
  }, [statsBySource?.changes_by_agency, statsBySource?.changes_by_source]);

  const timelineData = React.useMemo(() => {
    const raw = statsTimeline?.changes_timeline ?? [];
    const countByDate: Record<string, number> = {};
    raw.forEach((d: { date: string; count: number }) => {
      const dateStr = officialDateKey(d.date) || '';
      if (dateStr) countByDate[dateStr] = Number(d.count) || 0;
    });

    const apiRange = periodToApiRange(effectiveChartPeriod('timeline'), publicationDateMin);
    const isTotal = !apiRange.start && !apiRange.end;

    if (isTotal) {
      return raw
        .map((d: { date: string; count: number }) => ({
          date: officialDateKey(d.date) || String(d.date),
          count: Number(d.count) || 0,
        }))
        .filter((d) => d.date)
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    const endKey = chartTimelineRangeEnd(apiRange.end);
    const startKey = apiRange.start || publicationDateMin || endKey;
    return fillPublicationDaysInRange(countByDate, startKey, endKey);
  }, [
    statsTimeline?.changes_timeline,
    effectiveChartPeriod,
    publicationDateMin,
  ]);

  const statsErrorMessage =
    statsError != null
      ? `Não foi possível carregar os indicadores: ${statsError.message}`
      : null;

  return (
    <Box component="div" sx={{ pb: { xs: 3, sm: 4 } }}>
      {statsPending && !statsErrorMessage ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Carregando indicadores do banco de dados…
        </Alert>
      ) : null}
      {statsErrorMessage ? (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetchStats()}>
              Tentar de novo
            </Button>
          }
        >
          {statsErrorMessage} Use apenas http://localhost:3000 (container legislacao-web-dev). Cmd+Shift+R para recarregar.
        </Alert>
      ) : null}
      {/* Header */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <RouterLink to="/radar" style={{ textDecoration: 'none' }}>
            <Chip label="Radar por cliente" size="small" color="primary" variant="outlined" sx={{ cursor: 'pointer' }} />
          </RouterLink>
        </Box>
      </Box>

      {/* Stats Cards: Mudanças (changes), Normas, Fontes */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <DashboardKpiCard
            title="Mudanças Hoje"
            value={stats?.changes_today || 0}
            icon={<History />}
            loading={statsPending}
            onClick={() => handleOpenCardDetails('today')}
            active={cardDetailsOpen && cardDetailsType === 'today'}
            barLabel="Mudanças hoje"
            breakdown={changesTodayBreakdown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <DashboardKpiCard
            title={`Mudanças Este Mês${stats?.changes_kpi_month_label ? ` (${stats.changes_kpi_month_label})` : ''}`}
            value={stats?.changes_this_month || 0}
            icon={<Assessment />}
            loading={statsPending}
            onClick={() => handleOpenCardDetails('month')}
            active={cardDetailsOpen && cardDetailsType === 'month'}
            barLabel="Por data de publicação"
            breakdown={changesMonthBreakdown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <DashboardKpiCard
            title={`Mudanças no Ano${stats?.changes_kpi_year_label ? ` (${stats.changes_kpi_year_label})` : ''}`}
            value={stats?.changes_this_year ?? 0}
            icon={<EventIcon />}
            loading={statsPending}
            onClick={() => handleOpenCardDetails('total')}
            active={cardDetailsOpen && cardDetailsType === 'total'}
            barLabel="Por data de publicação"
            breakdown={changesYearBreakdown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <DashboardKpiCard
            title="Normas Monitoradas"
            value={stats?.total_norms ?? 0}
            icon={<Gavel />}
            loading={statsPending}
            onClick={() => handleOpenCardDetails('total')}
            active={cardDetailsOpen && cardDetailsType === 'total'}
            barLabel="Normas monitoradas"
            breakdown={normsBreakdownContextual}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <DashboardKpiCard
            title="Fontes Ativas"
            value={`${fontesAtivasKpi.active}/${fontesAtivasKpi.total}`}
            icon={<SourceIcon />}
            loading={statsPending}
            onClick={() => handleOpenCardDetails('sources')}
            active={cardDetailsOpen && cardDetailsType === 'sources'}
            barLabel="Fontes ativas"
          />
        </Grid>
      </Grid>
      {activeAgencyNames.length === 0 && (
        <Paper elevation={0} sx={{ p: 1, mb: 1.5, border: `1px solid ${theme.palette.info.main}`, bgcolor: alpha(theme.palette.info.main, 0.06) }}>
          <Typography variant="body2" color="info.main">
            Os cards acima usam as <strong>agências de interesse</strong> que você definir em{' '}
            <RouterLink to="/preferences" style={{ color: 'inherit', fontWeight: 600 }}>Preferências</RouterLink>.
            Sem agências selecionadas, são consideradas todas as fontes.
          </Typography>
        </Paper>
      )}

      {/* Charts Row: mesma altura para os dois cards */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }} alignItems="stretch">
        {/* Timeline Chart */}
        <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
          <motion.div style={{ width: '100%', minHeight: '100%', display: 'flex' }} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <Assessment sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#303030', textTransform: 'uppercase' }}>
                  Mudanças ao longo do tempo
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080' }}>
                    Quantidade de mudanças por dia (considerando as agências de interesse).
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9a9a9a' }} display="block">
                    Tipos: {CHANGE_TYPE_CATEGORIES.map(t => t.label).join(', ')}.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={chartUseGlobalPeriod.timeline ? 'Filtro global' : 'Período próprio'}
                    color={chartUseGlobalPeriod.timeline ? 'primary' : 'default'}
                    variant={chartUseGlobalPeriod.timeline ? 'filled' : 'outlined'}
                    onClick={() =>
                      setChartUseGlobalPeriod((prev) => ({ ...prev, timeline: !prev.timeline }))
                    }
                    sx={{ height: 26, fontSize: 11, fontWeight: 700 }}
                  />
                  <FiltroPorPeriodo
                    value={effectiveChartPeriod('timeline')}
                    onChange={(value) => handleChartPeriodChange('timeline', value)}
                    label="FILTRAR POR:"
                    compact
                    totalSinceDate={publicationDateMin}
                  />
                </Box>
              </Box>
              {(isLoadingTimeline || isFetchingTimeline) && !statsTimeline ? (
                <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 300 }} />
              ) : (
                <Box sx={{ flex: 1, minHeight: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timelineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9e9e9" />
                      <XAxis
                        dataKey="date"
                        stroke="#b0b0b0"
                        tick={{ fontSize: 11, fill: '#808080', fontWeight: 600 }}
                        tickFormatter={(value) => formatOfficialDateShortBR(String(value))}
                      />
                      <YAxis
                        stroke="#b0b0b0"
                        tick={{ fontSize: 11, fill: '#808080', fontWeight: 600 }}
                        domain={[0, 'auto']}
                        allowDecimals={false}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 10,
                          boxShadow: '0px 8px 18px rgba(0,0,0,0.08)',
                        }}
                        labelFormatter={(value) => formatOfficialDateBR(String(value))}
                        formatter={(value: number) => [value, 'Mudanças']}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2.5}
                        fill="url(#colorCount)"
                        dot={{ fill: theme.palette.primary.main, strokeWidth: 1.5, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </motion.div>
        </Grid>

        {/* Tipo de mudança */}
        <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
          <motion.div style={{ width: '100%', minHeight: '100%', display: 'flex' }} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <DashboardChartCardShell
              icon={<FilterList />}
              title="Tipo de mudança"
              subtitle="Quantidade e percentual em gráficos separados."
              periodValue={effectiveChartPeriod('type')}
              onPeriodChange={(value) => handleChartPeriodChange('type', value)}
              totalSinceDate={publicationDateMin}
              loading={(isLoadingByType || isFetchingByType) && !statsByType}
              useGlobalPeriod={chartUseGlobalPeriod.type}
              onUseGlobalPeriodChange={(useGlobal) =>
                setChartUseGlobalPeriod((prev) => ({ ...prev, type: useGlobal }))
              }
            >
              <DistributionChartsPanel
                categories={CHANGE_TYPE_CATEGORIES}
                rawCounts={statsByType?.changes_by_type}
                emptyHint={chartEmptyHint('type')}
              />
            </DashboardChartCardShell>
          </motion.div>
        </Grid>
      </Grid>

      {/* Mudanças por agência + tipo de norma */}
      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            style={{ height: '100%', display: 'flex' }}
          >
            <DashboardChartCardShell
              icon={<SourceIcon />}
              title="Mudanças por agência"
              subtitle="Quantidade de mudanças por agência (data de publicação)"
              periodValue={effectiveChartPeriod('source')}
              onPeriodChange={(value) => handleChartPeriodChange('source', value)}
              totalSinceDate={publicationDateMin}
              loading={(isLoadingBySource || isFetchingBySource) && !statsBySource}
              skeletonHeight={300}
              useGlobalPeriod={chartUseGlobalPeriod.source}
              onUseGlobalPeriodChange={(useGlobal) =>
                setChartUseGlobalPeriod((prev) => ({ ...prev, source: useGlobal }))
              }
            >
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={changesByAgencyData.length ? changesByAgencyData : [{ name: '—', value: 0 }]}
                    margin={{ top: 5, right: 30, left: 44, bottom: 56 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9e9e9" />
                    <XAxis
                      dataKey="name"
                      stroke="#b0b0b0"
                      tick={{ fontSize: 10, fill: '#808080', fontWeight: 600 }}
                      angle={-35}
                      textAnchor="end"
                      height={56}
                      label={{ value: 'Agências', position: 'insideBottom', offset: -8, style: { fontSize: 11, fill: '#808080', fontWeight: 700 } }}
                    />
                    <YAxis
                      stroke="#b0b0b0"
                      tick={{ fontSize: 10, fill: '#808080', fontWeight: 600 }}
                      domain={[0, 'auto']}
                      label={{ value: 'Quantidade', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#808080', fontWeight: 700 } }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 10,
                        boxShadow: '0px 8px 18px rgba(0,0,0,0.08)',
                      }}
                      formatter={(value: number) => [value, 'Mudanças']}
                    />
                    <Bar
                      dataKey="value"
                      fill={theme.palette.primary.main}
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                {!isLoadingBySource && !isFetchingBySource && changesByAgencyData.length === 0 && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080', textAlign: 'center', mt: 1 }}>
                    {chartEmptyHint('source')}
                  </Typography>
                )}
              </Box>
            </DashboardChartCardShell>
          </motion.div>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
          <motion.div
            style={{ width: '100%', minHeight: '100%', display: 'flex' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <DashboardChartCardShell
              icon={<Gavel />}
              title="Mudanças por tipo de norma"
              subtitle="Quantidade e percentual por tipo de norma (lei, decreto, instrução normativa…)."
              periodValue={effectiveChartPeriod('lawType')}
              onPeriodChange={(value) => handleChartPeriodChange('lawType', value)}
              totalSinceDate={publicationDateMin}
              loading={(isLoadingByLawType || isFetchingByLawType) && !statsByLawType}
              useGlobalPeriod={chartUseGlobalPeriod.lawType}
              onUseGlobalPeriodChange={(useGlobal) =>
                setChartUseGlobalPeriod((prev) => ({ ...prev, lawType: useGlobal }))
              }
            >
              <DistributionChartsPanel
                categories={LAW_TYPE_CATEGORIES}
                rawCounts={statsByLawType?.changes_by_law_type}
                tableTypeColumnLabel="Tipo de norma"
                tableMaxVisibleRows={4}
                emptyHint={chartEmptyHint('lawType')}
              />
            </DashboardChartCardShell>
          </motion.div>
        </Grid>
      </Grid>

      {/* Filtros da tabela de mudanças (PV/PA) */}
      <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <MudancasMonitoramentoFiltros
              periodFilter={changesPeriodFilter}
              onPeriodChange={(value) => {
                clearFocusHighlightRef.current();
                setChangesPeriodFilter(value);
                tableState.handlePageChange(1);
              }}
              actionsMonitoredNormAssuntoFilter={monitoredNormAssuntoChanges}
              onActionsMonitoredNormAssuntoFilterClear={() => {
                clearFocusHighlightRef.current();
                setMonitoredNormAssuntoChanges(false);
                tableState.handlePageChange(1);
              }}
              actionsMonitoredAgencyFilter={monitoredAgencyChanges}
              onActionsMonitoredAgencyFilterClear={() => {
                clearFocusHighlightRef.current();
                setMonitoredAgencyChanges(false);
                tableState.handlePageChange(1);
              }}
              greeningQaFilter={greeningQaOnlyChanges}
              onGreeningQaFilterClear={() => {
                clearFocusHighlightRef.current();
                setGreeningQaOnlyChanges(false);
                tableState.handlePageChange(1);
              }}
              assuntoFilter={assuntoFilterChanges}
              onAssuntoFilterChange={(value) => {
                clearFocusHighlightRef.current();
                setAssuntoFilterChanges(value);
                tableState.handlePageChange(1);
              }}
              keywordFilter={keywordFilterChanges}
              onKeywordFilterChange={(value) => {
                clearFocusHighlightRef.current();
                setKeywordFilterChanges(value);
                tableState.handlePageChange(1);
              }}
              onOpenAgencyMonitoring={agencyMonitoring.openAgencyMonitoring}
              onOpenMonitoringHistory={agencyMonitoring.openMonitoringHistoryAssuntos}
              publicationDateMin={publicationDateMin}
              extraActiveFilters={columnFilterActiveChips}
              onClearExtraFilters={clearAllColumnFilters}
            />
          </motion.div>
        </Grid>
      </Grid>

      {/* Recent Changes Table */}
      <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {outsideFiltersBanner && (
              <ChangeFocusOutsideFiltersBanner
                label={outsideFiltersBanner.label}
                onShowInList={() => void showChangeInList()}
                loading={isResolvingFocus}
              />
            )}
            <MudancasLegislativasTableSection
              // Paginado: carrega só 10 por vez (ou page_size) e permite navegar pelo total (8k+).
              data={tableRowsForDisplay}
              loading={tableLoading}
              onRowClick={handleTableRowClick}
              onViewDetails={handleTableViewDetails}
              onSendEmail={handleTableSendEmail}
              showActions={true}
              dense={false}
              stickyHeader={false}
              monitoringStatus={{}}
              monitoredAssuntoLabels={monitoredAssuntoLabels}
              actionsColumnFilters={{
                monitoredNormAssuntoActive: monitoredNormAssuntoChanges,
                monitoredAgencyActive: monitoredAgencyChanges,
                greeningQaActive: greeningQaOnlyChanges,
                onMonitoredNormAssuntoToggle: handleMonitoredNormAssuntoToggle,
                onMonitoredAgencyToggle: handleMonitoredAgencyToggle,
                onGreeningQaToggle: () => {
                  clearFocusHighlightRef.current();
                  setGreeningQaOnlyChanges((v) => !v);
                  tableState.handlePageChange(1);
                },
              }}
              pagination={{
                enabled: true,
                page: tableState.page,
                rowsPerPage: tableState.rowsPerPage,
                totalRows: tableTotalRows,
                onPageChange: handleTablePageChange,
                onRowsPerPageChange: handleTableRowsPerPageChange,
              }}
              serverSort={{
                sortBy: changesSortBy,
                sortOrder: changesSortOrder,
                onRequestSort: (sortBy, sortOrder) => {
                  clearFocusHighlight();
                  setChangesSortBy(sortBy);
                  setChangesSortOrder(sortOrder);
                  tableState.handlePageChange(1);
                },
              }}
              stateFilterOptions={changesCatalogs.states}
              agencyFilterCatalog={changesCatalogs.agencyCatalog}
              lawTypeFilterOptions={changesCatalogs.lawTypes}
              changeTypeFilterOptions={changesCatalogs.changeTypes}
              onColumnFiltersChange={handleChangesColumnFiltersChange}
              controlledColumnFilters={changesColumnFilters}
              resetColumnFiltersNonce={columnFiltersResetNonce}
              modalProps={{
                showEmail: true,
                showDownload: false,
                showHistory: false,
                showMonitoring: false,
              }}
              highlightedChangeId={highlightedChangeId}
            />
          </motion.div>
        </Grid>
      </Grid>

      {/* Card Details Modal */}
      <Dialog
        open={cardDetailsOpen}
        onClose={handleCloseCardDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {cardDetailsType === 'today' && 'Mudanças de Hoje'}
              {cardDetailsType === 'month' && 'Mudanças Este Mês'}
              {cardDetailsType === 'total' && 'Total de Mudanças (Recentes)'}
              {cardDetailsType === 'sources' && 'Fontes Ativas'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cardDetailsType === 'sources'
                ? sourcesFontesAtivasCaption
                : `${cardDetailsTotal} mudança(s) encontrada(s)${
                    cardDetailsTotalPages > 1 ? ` · página ${cardDetailsPage} de ${cardDetailsTotalPages}` : ''
                  }`}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCardDetails} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {cardDetailsType === 'sources' ? (
            <Box>
              {(() => {
                const sortSourcesByNormThenName = (list: any[]) =>
                  [...list].sort((a: any, b: any) => {
                    const na = Number(a?.total_normas ?? 0);
                    const nb = Number(b?.total_normas ?? 0);
                    const pa = na > 0 ? 0 : 1;
                    const pb = nb > 0 ? 0 : 1;
                    if (pa !== pb) return pa - pb;
                    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'pt-BR');
                  });

                const federal = sourcesSummary as any[];
                const state = stateSourcesForModal;
                const municipal = sourcesSummaryMunicipal as any[];

                const federalSorted = sortSourcesByNormThenName(federal);
                const stateSorted = sortSourcesByNormThenName(state);
                const municipalSorted = sortSourcesByNormThenName(municipal);

                const stateGroups = groupSourcesByState(stateSorted);
                const municipalGroups = (() => {
                  const byState = groupSourcesByState(municipalSorted);
                  return byState.map(([stateName, items]) => {
                    const byCity: Record<string, any[]> = {};
                    (items || []).forEach((s: any) => {
                      const city = String((s as any)?.municipality ?? '').trim() || '—';
                      if (!byCity[city]) byCity[city] = [];
                      byCity[city].push(s);
                    });
                    Object.keys(byCity).forEach((k) => {
                      byCity[k] = byCity[k].sort((a, b) =>
                        String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'pt-BR'),
                      );
                    });
                    const cities = Object.entries(byCity).sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));
                    return [stateName, cities] as const;
                  });
                })();

                const af = stats?.active_sources_federal ?? stats?.active_sources ?? 0;
                const tf = stats?.total_sources_federal ?? stats?.total_sources ?? federal.length;
                /** Aba estadual: catálogo filtrado na UI + nenhuma fonte ativa até o BD consolidar. */
                const astStateUi = 0;
                const tstStateUi = state.length;
                const am = stats?.active_sources_municipal ?? 0;
                const tm = stats?.total_sources_municipal ?? municipal.length;

                const zeroKeys = federalSorted
                  .filter((s: any) => Number(s?.total_normas ?? 0) === 0)
                  .map((s: any) => String(s?.key ?? '').trim())
                  .filter(Boolean);

                const summaryRight =
                  sourcesTab === 'federal'
                    ? `${af}/${tf} ativa(s) / cadastrada(s)`
                    : sourcesTab === 'state'
                      ? `${astStateUi}/${tstStateUi} ativa(s) / cadastrada(s)`
                      : `${am}/${tm} ativa(s) / cadastrada(s)`;

                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {sourcesTab === 'federal' ? (
                          <>
                            <strong>{tf}</strong> fonte(s) federal(is) no catálogo (BD) · <strong>{af}</strong> com norma(s)
                            monitorada(s).
                          </>
                        ) : sourcesTab === 'state' ? (
                          <>
                            <strong>{tstStateUi}</strong> fonte(s) estadual(is) no catálogo (BD) · <strong>{astStateUi}</strong> com norma(s)
                            monitorada(s).
                          </>
                        ) : (
                          <>
                            <strong>{tm}</strong> fonte(s) municipal(is) no catálogo (BD) · <strong>{am}</strong> com norma(s)
                            monitorada(s).
                          </>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summaryRight}
                      </Typography>
                    </Box>
                    {sourcesTab === 'federal' && zeroKeys.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Agências sem norma monitorada (total 0): <strong>{zeroKeys.join(', ')}</strong>
                      </Typography>
                    )}

                    <Tabs
                      value={sourcesTab}
                      onChange={(_, v) => setSourcesTab(v)}
                      sx={{ mb: 1.5 }}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab value="federal" label={`Federais (${af}/${tf})`} />
                      <Tab value="state" label={`Estaduais (${astStateUi}/${tstStateUi})`} />
                      <Tab value="municipal" label={`Municipais (${am}/${tm})`} />
                    </Tabs>

                    <TableContainer>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 240 }} />
                          <col style={{ width: 88 }} />
                          <col style={{ width: 68 }} />
                          <col style={{ width: 80 }} />
                          <col style={{ width: 92 }} />
                          <col style={{ width: 70 }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: 86 }} />
                        </colgroup>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Nome</strong></TableCell>
                            <TableCell align="right"><strong>Qtde Total</strong></TableCell>
                            <TableCell align="right"><strong>Leis</strong></TableCell>
                            <TableCell align="right"><strong>Decretos</strong></TableCell>
                            <TableCell align="right"><strong>Resoluções</strong></TableCell>
                            <TableCell align="right"><strong>Outras</strong></TableCell>
                            <TableCell><strong>Chave</strong></TableCell>
                            <TableCell><strong>URL Base</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sourcesTab === 'federal' ? (
                            sourcesSummaryError ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="error">
                                    Erro ao carregar fontes federais: {(sourcesSummaryError as Error).message}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : isLoadingSourcesSummary && federalSorted.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="text.secondary">
                                    Carregando fontes federais…
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : federalSorted.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="text.secondary">
                                    Nenhuma fonte federal no catálogo.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              <>
                              {federalSorted.map((org: any) => {
                                const n = Number(org?.total_normas ?? 0);
                                return (
                                  <TableRow key={org.key} sx={{ opacity: n === 0 ? 0.85 : 1 }}>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        {org.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={n}
                                        size="small"
                                        variant="outlined"
                                        color={n === 0 ? 'default' : 'primary'}
                                      />
                                    </TableCell>
                                    <TableCell align="right">{org.leis ?? 0}</TableCell>
                                    <TableCell align="right">{org.decretos ?? 0}</TableCell>
                                    <TableCell align="right">{org.resolucoes ?? 0}</TableCell>
                                    <TableCell align="right">{org.outras ?? 0}</TableCell>
                                    <TableCell>
                                      <Chip label={org.key} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                      {org.base_url && org.base_url !== 'N/I' ? (
                                        <Link href={org.base_url} target="_blank" rel="noopener noreferrer">
                                          Ver site
                                        </Link>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">N/I</Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {(() => {
                                // Reconciliação com o card «Normas monitoradas»: o card conta
                                // TODAS as normas federais monitoradas; a tabela lista só as
                                // agências do catálogo. A diferença são normas de órgãos federais
                                // fora do catálogo (ex.: Minas e Energia, Defesa, Anvisa). Sem
                                // esta linha, a soma da tabela não bate com o card.
                                const shown = federalSorted.reduce(
                                  (s: number, o: any) => s + Number(o?.total_normas ?? 0),
                                  0,
                                );
                                const outros = Number(stats?.total_norms ?? 0) - shown;
                                if (outros <= 0) return null;
                                return (
                                  <TableRow key="__outros_federais__">
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        Outros órgãos federais
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        Órgãos federais fora do catálogo da UI
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip label={outros} size="small" variant="outlined" color="primary" />
                                    </TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">—</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">N/I</Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })()}
                              </>
                            )
                          ) : sourcesTab === 'state' ? (
                            sourcesSummaryStateError ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="error">
                                    Erro ao carregar fontes estaduais: {(sourcesSummaryStateError as Error).message}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : isLoadingSourcesSummaryState && stateGroups.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="text.secondary">
                                    Carregando agências estaduais…
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : stateGroups.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography variant="body2" color="text.secondary">
                                    Nenhuma agência estadual no catálogo.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              stateGroups.flatMap(([stateName, items]) => [
                                <TableRow key={`group-${stateName}`}>
                                  <TableCell colSpan={8} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: '#5a5a5a' }}>
                                      {stateName}
                                    </Typography>
                                  </TableCell>
                                </TableRow>,
                                ...items.map((source: any) => {
                                  const n = Number(source?.total_normas ?? 0);
                                  return (
                                    <TableRow key={source.id} sx={{ opacity: n === 0 ? 0.85 : 1 }}>
                                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2" noWrap>{source.name}</Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          label={n}
                                          size="small"
                                          variant="outlined"
                                          color={n === 0 ? 'default' : 'primary'}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{source.leis ?? 0}</TableCell>
                                      <TableCell align="right">{source.decretos ?? 0}</TableCell>
                                      <TableCell align="right">{source.resolucoes ?? 0}</TableCell>
                                      <TableCell align="right">{source.outras ?? 0}</TableCell>
                                      <TableCell>
                                        <Chip label={source.key} size="small" variant="outlined" />
                                      </TableCell>
                                      <TableCell>
                                        {source.base_url && source.base_url !== 'N/I' ? (
                                          <Link href={source.base_url} target="_blank" rel="noopener noreferrer">
                                            Ver site
                                          </Link>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary">N/I</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                }),
                              ])
                            )
                          ) : sourcesSummaryMunicipalError ? (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <Typography variant="body2" color="error">
                                  Erro ao carregar fontes municipais: {(sourcesSummaryMunicipalError as Error).message}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : isLoadingSourcesSummaryMunicipal && municipalSorted.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">
                                  Carregando agências municipais…
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : municipalGroups.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">
                                  Nenhuma agência municipal no catálogo.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            municipalGroups.flatMap(([stateName, cities]) => [
                              <TableRow key={`m-group-${stateName}`}>
                                <TableCell colSpan={8} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: '#5a5a5a' }}>
                                    {stateName}
                                  </Typography>
                                </TableCell>
                              </TableRow>,
                              ...cities.flatMap(([cityName, items]) => [
                                <TableRow key={`m-city-${stateName}-${cityName}`}>
                                  <TableCell colSpan={8} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#5a5a5a' }}>
                                      {cityName}
                                    </Typography>
                                  </TableCell>
                                </TableRow>,
                                ...items.map((source: any) => {
                                  const n = Number(source?.total_normas ?? 0);
                                  return (
                                    <TableRow key={source.id} sx={{ opacity: n === 0 ? 0.85 : 1 }}>
                                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2" noWrap>{source.name}</Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          label={n}
                                          size="small"
                                          variant="outlined"
                                          color={n === 0 ? 'default' : 'primary'}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{source.leis ?? 0}</TableCell>
                                      <TableCell align="right">{source.decretos ?? 0}</TableCell>
                                      <TableCell align="right">{source.resolucoes ?? 0}</TableCell>
                                      <TableCell align="right">{source.outras ?? 0}</TableCell>
                                      <TableCell>
                                        <Chip label={source.key} size="small" variant="outlined" />
                                      </TableCell>
                                      <TableCell>
                                        {source.base_url && source.base_url !== 'N/I' ? (
                                          <Link href={source.base_url} target="_blank" rel="noopener noreferrer">
                                            Ver site
                                          </Link>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary">N/I</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                }),
                              ]),
                            ])
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </Box>
          ) : (
            // Display Changes List
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell><strong>Agência</strong></TableCell>
                    <TableCell><strong>Tipo de Norma</strong></TableCell>
                    <TableCell><strong>Número</strong></TableCell>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cardDetailsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          Nenhuma mudança encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cardDetailsList.map((change: any) => (
                      <TableRow 
                        key={change.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <TableCell>
                          <Chip label={change.state} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{change.agency}</TableCell>
                        <TableCell>
                          <Chip
                            label={parseLegislationData(change.law).type}
                            size="small"
                            color={getLegislationColor(parseLegislationData(change.law).type)}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {parseLegislationData(change.law).number}
                        </TableCell>
                        <TableCell>
                          {formatOfficialDateBR(change.date)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={change.change}
                            size="small"
                            color={
                              change.change === 'Criada' ? 'success' :
                              change.change === 'Atualizada' ? 'info' :
                              'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <VerDetalhesComDataPublicacao
                            dataPublicacao={change.published_at ?? change.date}
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleCloseCardDetails();
                                tableState.handleViewDetails(change);
                              }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </VerDetalhesComDataPublicacao>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {cardDetailsType !== 'sources' && cardDetailsTotalPages > 1 ? (
            <Pagination
              count={cardDetailsTotalPages}
              page={cardDetailsPage}
              onChange={(_, p) => {
                if (cardDetailsType === 'today' || cardDetailsType === 'month' || cardDetailsType === 'total') {
                  void loadCardChangesPage(cardDetailsType, p);
                }
              }}
              size="small"
              color="primary"
              disabled={cardDetailsLoading}
              showFirstButton
              showLastButton
            />
          ) : (
            <span />
          )}
          <Button onClick={handleCloseCardDetails} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <Dialog
        open={tableState.detailsOpen && !!tableState.selectedChange}
        onClose={closeChangeDetailsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            position: 'relative',
            maxHeight: 'min(90vh, 900px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Detalhes da Mudança Legislativa
            </Typography>
            {tableState.selectedChange && (
              <Typography variant="caption" color="text.secondary">
                ID: {tableState.selectedChange?.id}
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeChangeDetailsModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        {tableState.selectedChange && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={changeDetailsTab}
              onChange={(_, value) => setChangeDetailsTab(value)}
              aria-label="Abas de detalhes da mudança"
            >
              <Tab label="Detalhes" />
              <Tab
                icon={<GreeningIcon sx={{ fontSize: 18 }} />}
                label={<GreeningBrandLabel fontWeight={600} fontSize="0.875rem" />}
                iconPosition="start"
              />
              <Tab icon={<History />} label="LOGs" iconPosition="start" />
            </Tabs>
          </Box>
        )}
        <DialogContent
          sx={{
            pt: 2,
            pb: 2,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {tableState.selectedChange && (
            <Box>
              {changeDetailsTab === 0 && (
                <>
              {/* Header Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Estado
                  </Typography>
                  <Chip 
                    label={tableState.selectedChange?.state} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tipo de Mudança
                  </Typography>
                  <Chip
                    label={tableState.selectedChange?.change}
                    size="small"
                    color={
                      tableState.selectedChange?.change === 'Criada' ? 'success' :
                      tableState.selectedChange?.change === 'Atualizada' ? 'info' :
                      'error'
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </Grid>

              {/* Main Info */}
              {(() => {
                const legislation = parseLegislationData(tableState.selectedChange?.law || '');
                return (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Tipo de Norma
                      </Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                        {legislation.type}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Número
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {legislation.number}
                      </Typography>
                    </Box>
                  </>
                );
              })()}

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Título
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {tableState.selectedChange?.title}
                </Typography>
              </Box>

              {tableState.selectedChange?.summary && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Ementa
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {tableState.selectedChange?.summary}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Assunto
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {resolveChangeAssuntoDisplay(
                    tableState.selectedChange?.subject,
                    tableState.selectedChange?.area_tematica,
                  ) || 'Não informado'}
                </Typography>
              </Box>

              {/* Dates */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data de Detecção
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                    <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {formatOfficialDateBR(tableState.selectedChange?.date)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data de Vigência
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                    {tableState.selectedChange?.effective_date ? (
                      <>
                        <CalendarToday sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                        <Typography variant="body2" fontWeight={500} color="primary">
                          {formatOfficialDateBR(tableState.selectedChange?.effective_date)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Não informada
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Additional Info */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Agência / Órgão
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {tableState.selectedChange?.agency}
                </Typography>
                {tableState.selectedChange?.agencies_linked ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Demais órgãos vinculados à norma
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {tableState.selectedChange.agencies_linked}
                    </Typography>
                  </>
                ) : null}
              </Box>

              {/* Documento Oficial DOU */}
              {tableState.selectedChange?.dou_ref && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Documento Oficial - Diário Oficial da União
                    </Typography>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        mt: 1.5,
                        p: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.05),
                        borderColor: theme.palette.error.main,
                        borderWidth: 1,
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <PictureAsPdf sx={{ fontSize: 40, color: 'error.main', mt: 0.5 }} />
                        <Box flex={1}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            DOU - {tableState.selectedChange?.change === 'Criada' ? 'Publicação da Criação' : 
                                  tableState.selectedChange?.change === 'Atualizada' ? 'Publicação da Atualização' :
                                  'Publicação da Revogação'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Referência: {tableState.selectedChange?.dou_ref}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Este é o documento oficial que estabelece a {tableState.selectedChange?.change.toLowerCase()} da legislação
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Download />}
                            onClick={() => {
                              const douUrl = `https://www.in.gov.br/leiturajornal?data=${tableState.selectedChange?.date}&secao=1`;
                              window.open(douUrl, '_blank');
                            }}
                            color="error"
                            sx={{ mt: 1.5 }}
                          >
                            Abrir PDF do DOU
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </>
              )}

              {/* Link da Legislação */}
              {tableState.selectedChange?.url && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Texto da Legislação
                    </Typography>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        mt: 1.5,
                        p: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: theme.palette.primary.main,
                        borderWidth: 1,
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <LinkIcon sx={{ fontSize: 40, color: 'primary.main', mt: 0.5 }} />
                        <Box flex={1}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            {tableState.selectedChange?.law}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {tableState.selectedChange?.title}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<OpenInNew />}
                            onClick={() => tableState.selectedChange?.url && window.open(tableState.selectedChange.url, '_blank')}
                            color="primary"
                            sx={{ mt: 1.5 }}
                          >
                            Abrir Texto Completo
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </>
              )}

              {!tableState.selectedChange?.url && !tableState.selectedChange?.dou_ref && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    Nenhum documento disponível
                  </Typography>
                </>
              )}
                </>
              )}

              {changeDetailsTab === 1 && (
                <GreeningTabPanel
                  legislationId={tableState.selectedChange?.legislation_id}
                  greening={changeDetailsGreening}
                  subject={tableState.selectedChange?.title}
                />
              )}

              {changeDetailsTab === 2 && (() => {
                const assuntoLabel = resolveChangeAssuntoDisplay(
                  tableState.selectedChange?.subject,
                  tableState.selectedChange?.area_tematica,
                );
                const assuntoKey = assuntoLabel ? normalizeAssuntoKey(assuntoLabel) : null;
                const isMonitored = assuntoKey ? monitoredAssuntoLabels.has(assuntoKey) : false;
                return (
                  <ChangeDetailLogsPanel
                    assuntoKey={assuntoKey}
                    assuntoLabel={assuntoLabel}
                    changeId={tableState.selectedChange?.id}
                    isMonitored={isMonitored}
                    enabled={tableState.detailsOpen}
                  />
                );
              })()}
              {changeDetailsLegislationId && <Box aria-hidden sx={{ height: 88, flexShrink: 0 }} />}
            </Box>
          )}
        </DialogContent>
        {changeDetailsLegislationId && (
          <Box
            sx={{
              position: 'absolute',
              right: 20,
              bottom: 76,
              zIndex: 10,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            <GreeningChatFab greening={changeDetailsGreening} />
          </Box>
        )}
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => tableState.selectedChange && tableState.handleSendEmail({ to: '', subject: '', item: tableState.selectedChange })}
            variant="contained"
            startIcon={<Email />}
            color="primary"
          >
            Enviar por E-mail
          </Button>
          <Button onClick={closeChangeDetailsModal} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ChangeEmailSendDialog
        open={tableState.emailDialogOpen}
        onClose={tableState.handleCloseEmailDialog}
        change={tableState.selectedChange}
      />

      <AgencyMonitoringSuiteDialogs {...agencyMonitoring} />
    </Box>
  );
}
